import * as React from "react";
import {
  getWidgetDimensions,
  saveWidgetDimensions,
  DEFAULT_WIDTH,
  DEFAULT_HEIGHT,
} from "./widget-dimensions-storage";

// Minimum dimensions to prevent unusable widget sizes
const MIN_WIDTH = 200;
const MIN_HEIGHT = 200;

export type ResizeEdge =
  | "n"
  | "s"
  | "e"
  | "w"
  | "ne"
  | "nw"
  | "se"
  | "sw";

export type UseResizableDimensionsOptions = {
  defaultWidth?: number;
  defaultHeight?: number;
  isLeft: boolean;
  enabled: boolean;
  getDimensions?: () => { width: number; height: number } | null;
  saveDimensions?: (dims: { width: number; height: number }) => void;
};

export type PositionAdjustment = {
  deltaX: number;
  deltaY: number;
};

export type UseResizableDimensionsReturn = {
  width: number;
  height: number;
  isResizing: boolean;
  positionAdjustment: PositionAdjustment;
  startResize: (edge: ResizeEdge, event: React.PointerEvent) => void;
  resetToDefault: () => void;
  setDimensions: (dimensions: { width: number; height: number }) => void;
  clearPositionAdjustment: () => void;
};

type ResizeState = {
  edge: ResizeEdge;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  pointerId: number;
} | null;

export function useResizableDimensions(
  options: UseResizableDimensionsOptions
): UseResizableDimensionsReturn {
  const {
    defaultWidth = DEFAULT_WIDTH,
    defaultHeight = DEFAULT_HEIGHT,
    isLeft,
    enabled,
    getDimensions: customGetDimensions,
    saveDimensions: customSaveDimensions,
  } = options;

  const resolvedGetRef = React.useRef(customGetDimensions ?? getWidgetDimensions);
  const resolvedSaveRef = React.useRef(customSaveDimensions ?? saveWidgetDimensions);
  // Update refs on every render (before effects run)
  resolvedGetRef.current = customGetDimensions ?? getWidgetDimensions;
  resolvedSaveRef.current = customSaveDimensions ?? saveWidgetDimensions;

  const [dimensions, setDimensions] = React.useState(() => {
    if (!enabled) return { width: defaultWidth, height: defaultHeight };
    const stored = resolvedGetRef.current();
    return stored ?? { width: defaultWidth, height: defaultHeight };
  });

  const resizeStateRef = React.useRef<ResizeState>(null);
  const [isResizing, setIsResizing] = React.useState(false);
  const [positionAdjustment, setPositionAdjustment] = React.useState<PositionAdjustment>({ deltaX: 0, deltaY: 0 });
  const rafRef = React.useRef<number | null>(null);

  const calculateNewDimensions = React.useCallback(
    (
      edge: ResizeEdge,
      deltaX: number,
      deltaY: number,
      startWidth: number,
      startHeight: number
    ) => {
      let newWidth = startWidth;
      let newHeight = startHeight;

      // Vertical: north edge grows upward (height increases when dragging up)
      // Widget is anchored at bottom, so "up" means negative deltaY
      if (edge.includes("n")) {
        newHeight = startHeight - deltaY;
      } else if (edge.includes("s")) {
        newHeight = startHeight + deltaY;
      }

      // Horizontal depends on isLeft (widget anchor position)
      // isLeft=false: widget anchored at right edge
      //   - west edge grows left (width increases when dragging left = negative deltaX)
      //   - east edge does nothing useful (would push widget off screen)
      // isLeft=true: widget anchored at left edge
      //   - east edge grows right (width increases when dragging right = positive deltaX)
      //   - west edge does nothing useful (would push widget off screen)
      if (isLeft) {
        // Widget anchored at left - east edge expands right
        if (edge.includes("e")) {
          newWidth = startWidth + deltaX;
        } else if (edge.includes("w")) {
          newWidth = startWidth - deltaX;
        }
      } else {
        // Widget anchored at right - west edge expands left
        if (edge.includes("w")) {
          newWidth = startWidth - deltaX;
        } else if (edge.includes("e")) {
          newWidth = startWidth + deltaX;
        }
      }

      // Apply minimum constraints to prevent unusable sizes
      newWidth = Math.max(MIN_WIDTH, newWidth);
      newHeight = Math.max(MIN_HEIGHT, newHeight);

      return { width: newWidth, height: newHeight };
    },
    [isLeft]
  );

  const handlePointerMove = React.useCallback(
    (event: PointerEvent) => {
      const state = resizeStateRef.current;
      if (!state) return;

      // Cancel any pending RAF
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const deltaX = event.clientX - state.startX;
        const deltaY = event.clientY - state.startY;

        const newDimensions = calculateNewDimensions(
          state.edge,
          deltaX,
          deltaY,
          state.startWidth,
          state.startHeight
        );

        // Calculate position adjustments for left/bottom positioning
        // West edge: move left position with mouse to keep right edge fixed
        // South edge: move bottom position opposite to mouse to keep top edge fixed
        let posAdjustX = 0;
        let posAdjustY = 0;

        if (state.edge.includes("w")) {
          // Width change = newWidth - startWidth
          // Position change = -(width change) to keep right edge fixed
          posAdjustX = -(newDimensions.width - state.startWidth);
        }
        if (state.edge.includes("s")) {
          // Height change = newHeight - startHeight
          // Position change = -(height change) to keep top edge fixed
          posAdjustY = -(newDimensions.height - state.startHeight);
        }

        setDimensions(newDimensions);
        setPositionAdjustment({ deltaX: posAdjustX, deltaY: posAdjustY });
      });
    },
    [calculateNewDimensions]
  );

  const handlePointerUp = React.useCallback(
    (event: PointerEvent) => {
      const state = resizeStateRef.current;
      if (!state) return;

      // Release pointer capture
      const target = event.target as HTMLElement;
      if (target.hasPointerCapture?.(state.pointerId)) {
        target.releasePointerCapture(state.pointerId);
      }

      // Save final dimensions
      const deltaX = event.clientX - state.startX;
      const deltaY = event.clientY - state.startY;

      const finalDimensions = calculateNewDimensions(
        state.edge,
        deltaX,
        deltaY,
        state.startWidth,
        state.startHeight
      );

      setDimensions(finalDimensions);
      resolvedSaveRef.current(finalDimensions);

      // Clean up
      resizeStateRef.current = null;
      setIsResizing(false);

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      // Remove listeners
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    },
    [calculateNewDimensions, handlePointerMove],
  );

  const startResize = React.useCallback(
    (edge: ResizeEdge, event: React.PointerEvent) => {
      if (!enabled) return;

      event.preventDefault();
      event.stopPropagation();

      // Set pointer capture for smooth dragging
      const target = event.target as HTMLElement;
      target.setPointerCapture(event.pointerId);

      resizeStateRef.current = {
        edge,
        startX: event.clientX,
        startY: event.clientY,
        startWidth: dimensions.width,
        startHeight: dimensions.height,
        pointerId: event.pointerId,
      };

      setIsResizing(true);

      // Add listeners to document for reliable tracking
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    },
    [enabled, dimensions, handlePointerMove, handlePointerUp]
  );

  const resetToDefault = React.useCallback(() => {
    setDimensions({ width: defaultWidth, height: defaultHeight });
    resolvedSaveRef.current({ width: defaultWidth, height: defaultHeight });
  }, [defaultWidth, defaultHeight]);

  const setDimensionsTo = React.useCallback((newDimensions: { width: number; height: number }) => {
    setDimensions(newDimensions);
    resolvedSaveRef.current(newDimensions);
  }, []);

  const clearPositionAdjustment = React.useCallback(() => {
    setPositionAdjustment({ deltaX: 0, deltaY: 0 });
  }, []);

  // Update dimensions if enabled changes (e.g., entering fullscreen)
  React.useEffect(() => {
    if (enabled) {
      const stored = resolvedGetRef.current();
      if (stored) {
        setDimensions(stored);
      }
    }
  }, [enabled]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  return {
    width: dimensions.width,
    height: dimensions.height,
    isResizing,
    positionAdjustment,
    startResize,
    resetToDefault,
    setDimensions: setDimensionsTo,
    clearPositionAdjustment,
  };
}
